import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

class GiftCardRecipientForm extends Component {
  static DeliveryMode = {
    SELF: 'self',
    RECIPIENT: 'recipient_form',
  };

  #currentMode = GiftCardRecipientForm.DeliveryMode.SELF;
  #updateCharacterCountBound = null;
  #displayCartErrorBound = null;
  #cartUpdateBound = null;

  requiredRefs = [
    'myEmailButton',
    'recipientEmailButton',
    'recipientFields',
    'recipientEmail',
    'recipientName',
    'recipientMessage',
    'recipientSendOn',
  ];

  get #inputFields() {
    return [this.refs.recipientEmail, this.refs.recipientName, this.refs.recipientMessage, this.refs.recipientSendOn];
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeForm();

    this.#updateCharacterCountBound = () => this.#updateCharacterCount();
    this.refs.recipientMessage.addEventListener('input', this.#updateCharacterCountBound);

    this.#displayCartErrorBound = this.#displayCartError.bind(this);
    document.addEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);

    this.#cartUpdateBound = this.#handleCartUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartUpdateBound);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#updateCharacterCountBound) {
      this.refs.recipientMessage.removeEventListener('input', this.#updateCharacterCountBound);
      this.#updateCharacterCountBound = null;
    }

    if (this.#displayCartErrorBound) {
      document.removeEventListener(ThemeEvents.cartError, this.#displayCartErrorBound);
      this.#displayCartErrorBound = null;
    }

    if (this.#cartUpdateBound) {
      document.removeEventListener(ThemeEvents.cartUpdate, this.#cartUpdateBound);
      this.#cartUpdateBound = null;
    }
  }

  toggleRecipientForm(mode, _event) {
    if (!Object.values(GiftCardRecipientForm.DeliveryMode).includes(mode)) {
      throw new Error(`Invalid delivery mode: ${mode}`);
    }

    if (this.#currentMode === mode) return;

    this.#currentMode = mode;
    this.#updateFormState();
  }

  #initializeForm() {
    this.#updateButtonStates(GiftCardRecipientForm.DeliveryMode.SELF);
    this.refs.recipientFields.hidden = true;
    this.#clearRecipientFields();
    this.#disableRecipientFields();
    this.#setDateConstraints();
  }

  #updateFormState() {
    const isRecipientMode = this.#currentMode === GiftCardRecipientForm.DeliveryMode.RECIPIENT;

    this.#updateButtonStates(this.#currentMode);
    this.refs.recipientFields.hidden = !isRecipientMode;

    if (isRecipientMode) {
      this.#enableRecipientFields();
      this.#updateCharacterCount();
      this.#announce(Theme.translations?.recipient_form_fields_visible || 'Recipient form fields are now visible');
      this.refs.recipientEmail.focus();
    } else {
      this.#clearRecipientFields();
      this.#disableRecipientFields();
      this.#announce(Theme.translations?.recipient_form_fields_hidden || 'Recipient form fields are now hidden');
    }

    this.dispatchEvent(
      new CustomEvent('recipient:toggle', {
        detail: {
          mode: this.#currentMode,
          recipientFormVisible: isRecipientMode,
        },
        bubbles: true,
      })
    );
  }

  #updateButtonStates(mode) {
    this.refs.myEmailButton.checked = mode === GiftCardRecipientForm.DeliveryMode.SELF;
    this.refs.recipientEmailButton.checked = mode === GiftCardRecipientForm.DeliveryMode.RECIPIENT;
  }

  #clearRecipientFields() {
    for (const field of this.#inputFields) {
      field.value = '';
    }

    this.#updateCharacterCount();
    this.#clearErrorMessages();
  }

  #disableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = true;
      field.removeAttribute('required');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]')?.remove();

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = true;
      this.refs.timezoneOffset.value = '';
    }

    this.#clearErrorMessages();
  }

  #enableRecipientFields() {
    for (const field of this.#inputFields) {
      field.disabled = false;
    }

    this.refs.recipientEmail.setAttribute('required', 'required');

    if (!this.querySelector('input[name="properties[__shopify_send_gift_card_to_recipient]"]')) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[__shopify_send_gift_card_to_recipient]';
      input.value = 'true';
      this.appendChild(input);
    }

    if (this.refs.timezoneOffset) {
      this.refs.timezoneOffset.disabled = false;
      this.refs.timezoneOffset.value = new Date().getTimezoneOffset().toString();
    }

    this.#setDateConstraints();
  }

  #updateCharacterCount() {
    if (!this.refs.characterCount) return;

    const currentLength = this.refs.recipientMessage.value.length;
    const maxLength = this.refs.recipientMessage.maxLength;
    const template = this.refs.characterCount.getAttribute('data-template');

    if (!template) return;

    this.refs.characterCount.textContent = template
      .replace('[current]', currentLength.toString())
      .replace('[max]', maxLength.toString());
  }

  #setDateConstraints() {
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 90);

    this.refs.recipientSendOn.setAttribute('min', this.#formatDate(today));
    this.refs.recipientSendOn.setAttribute('max', this.#formatDate(maxDate));
  }

  #formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  #displayCartError(event) {
    if (!this.#isEventForThisForm(event)) return;

    const { message, description, errors } = event.detail?.data || {};

    if (errors && typeof errors === 'object') {
      this.#displayErrorMessage(message, errors);
    } else if (description && typeof description === 'object') {
      this.#displayErrorMessage(message, description);
    } else if (message || description) {
      this.#displayErrorMessage(message || description, description);
    }
  }

  #displayErrorMessage(title, body) {
    this.#clearErrorMessages();

    if (body && typeof body === 'object') {
      const fieldMap = {
        email: { inputRef: 'recipientEmail', errorRef: 'emailError' },
        'Recipient email': { inputRef: 'recipientEmail', errorRef: 'emailError' },
        'properties[Recipient email]': { inputRef: 'recipientEmail', errorRef: 'emailError' },
        name: { inputRef: 'recipientName', errorRef: 'nameError' },
        'Recipient name': { inputRef: 'recipientName', errorRef: 'nameError' },
        'properties[Recipient name]': { inputRef: 'recipientName', errorRef: 'nameError' },
        message: { inputRef: 'recipientMessage', errorRef: 'messageError' },
        Message: { inputRef: 'recipientMessage', errorRef: 'messageError' },
        'properties[Message]': { inputRef: 'recipientMessage', errorRef: 'messageError' },
        send_on: { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
        'Send on': { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
        'properties[Send on]': { inputRef: 'recipientSendOn', errorRef: 'sendOnError' },
      };

      for (const [field, errorMessages] of Object.entries(body)) {
        const fieldConfig = fieldMap[field];
        if (!fieldConfig) continue;

        const errorContainer = this.refs[fieldConfig.errorRef];
        const inputElement = this.refs[fieldConfig.inputRef];

        if (errorContainer instanceof HTMLElement) {
          const errorTextElement = errorContainer.querySelector('span');
          const message = Array.isArray(errorMessages) ? errorMessages.join(', ') : errorMessages;

          if (errorTextElement) {
            errorTextElement.textContent = `${message}.`;
          }

          errorContainer.classList.remove('hidden');
        }

        if (inputElement instanceof HTMLElement) {
          inputElement.setAttribute('aria-invalid', 'true');

          if (errorContainer instanceof HTMLElement && errorContainer.id) {
            inputElement.setAttribute('aria-describedby', errorContainer.id);
          }
        }
      }
    }

    this.#announce(title || Theme.translations?.recipient_form_error || 'There was an error with the form submission');
  }

  #clearErrorMessages() {
    const errorRefs = ['emailError', 'nameError', 'messageError', 'sendOnError'];

    for (const errorRef of errorRefs) {
      const errorContainer = this.refs[errorRef];

      if (errorContainer instanceof HTMLElement) {
        errorContainer.classList.add('hidden');
        const errorTextElement = errorContainer.querySelector('span');

        if (errorTextElement) {
          errorTextElement.textContent = '';
        }
      }
    }

    for (const field of this.#inputFields) {
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    }

    this.#announce('');
  }

  #handleCartUpdate(event) {
    if (event.detail?.data?.didError) return;

    if (this.#isEventForThisForm(event)) {
      this.#clearErrorMessages();
    }
  }

  #isEventForThisForm(event) {
    const form = this.closest('form');
    const sourceId = event.detail?.sourceId;
    const formId = form?.getAttribute('id');

    return !sourceId || !formId || sourceId === formId;
  }

  #announce(message) {
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = message;
    }
  }
}

if (!customElements.get('gift-card-recipient-form')) {
  customElements.define('gift-card-recipient-form', GiftCardRecipientForm);
}
